import type { ProductType, Store } from './store';

/**
 * EXTENSION CONTRACT: adding a sixth product kind costs exactly one union
 * member here, one PRODUCT_PRECEDENCE entry, one PRODUCT_CAPABILITIES entry,
 * and (only if it needs copy the three existing redemption styles do not
 * cover) one new REDEMPTION_COPY entry. Nothing else in this file, and no
 * template, should need to branch on the new kind directly.
 */
export type ProductKind = 'voucher' | 'virtual' | 'digital' | 'depositable' | 'physical';

export type StoreProducts = Partial<Record<ProductKind, ProductType | null>>;

/**
 * THE single place resolution order lives. Voucher is first: a reseller
 * store is a voucher store even if a legacy product key lingers alongside it.
 */
export const PRODUCT_PRECEDENCE: readonly ProductKind[] = ['voucher', 'virtual', 'digital', 'depositable', 'physical'] as const;

/**
 * Null-safe on `store`, `store.products` and each key — returns `null`
 * rather than throwing on a malformed/partial `products` object.
 */
export function resolveProductKind(store: Pick<Store, 'products'> | null | undefined): ProductKind | null {
  const products = store?.products;
  if (!products) return null;
  for (const kind of PRODUCT_PRECEDENCE) {
    if (products[kind] != null) return kind;
  }
  return null;
}

/**
 * Returns `null` (never `undefined`) so callers keep an honest
 * `ProductType | null` return type.
 */
export function resolveActiveProduct(store: Pick<Store, 'products'> | null | undefined): ProductType | null {
  const kind = resolveProductKind(store);
  if (!kind) return null;
  return store?.products?.[kind] ?? null;
}

export type RedemptionStyle = 'wallet' | 'in-store-qr' | 'voucher-code';

export interface ProductCapabilities {
  isInstantIssuance: boolean;
  canStoreInApp: boolean;
  redemptionStyle: RedemptionStyle;
}

interface CapabilityEntry {
  isInstantIssuance: boolean;
  canStoreInApp: boolean;
  /**
   * Is the API's `allowUserStoreInApp` the authority for this kind?
   *
   * REGRESSION TRAP: verified against all 23 stores on the test API,
   * `allowUserStoreInApp` is `true` ONLY on the voucher product and `false`
   * on every legacy-kind store — including several that display the "Mine
   * Gavekort-appen" copy today. So the flag must be consulted ONLY for the
   * kind(s) whose entry sets this `true` (voucher); every legacy kind keeps
   * its static `canStoreInApp` regardless of the API value.
   */
  storeInAppFromApi: boolean;
  redemptionStyle: RedemptionStyle;
}

export const PRODUCT_CAPABILITIES: Record<ProductKind, CapabilityEntry> = {
  // ZCOD create takes quantity 1-1000 synchronously → instant, so voucher
  // must never hit the "2-3 virkedager, behandles manuelt" bulk path.
  voucher: { isInstantIssuance: true, canStoreInApp: true, storeInAppFromApi: true, redemptionStyle: 'voucher-code' },
  virtual: { isInstantIssuance: true, canStoreInApp: true, storeInAppFromApi: false, redemptionStyle: 'wallet' },
  digital: { isInstantIssuance: false, canStoreInApp: true, storeInAppFromApi: false, redemptionStyle: 'in-store-qr' },
  depositable: { isInstantIssuance: false, canStoreInApp: true, storeInAppFromApi: false, redemptionStyle: 'in-store-qr' },
  physical: { isInstantIssuance: false, canStoreInApp: true, storeInAppFromApi: false, redemptionStyle: 'in-store-qr' },
};

// Preserves today's behaviour for a store with no resolvable product (the
// pre-load default, and any store whose products object has nothing set).
const DEFAULT_CAPABILITIES: ProductCapabilities = {
  isInstantIssuance: false,
  canStoreInApp: true,
  redemptionStyle: 'in-store-qr',
};

export function resolveCapabilities(store: Pick<Store, 'products'> | null | undefined): ProductCapabilities {
  const kind = resolveProductKind(store);
  if (!kind) return DEFAULT_CAPABILITIES;
  const entry = PRODUCT_CAPABILITIES[kind];
  const product = store?.products?.[kind] ?? null;
  return {
    isInstantIssuance: entry.isInstantIssuance,
    canStoreInApp: entry.storeInAppFromApi ? (product?.allowUserStoreInApp ?? entry.canStoreInApp) : entry.canStoreInApp,
    redemptionStyle: entry.redemptionStyle,
  };
}

/**
 * The validity period the buyer is shown before paying, or null to show none.
 * "fra kjøpsdato" is the safe floor: every kind's clock starts at purchase or
 * later (Visa's when the card is added to the app), so the card lasts at
 * least this long. Voucher states none: the provider sets its validity.
 */
export function resolveValidityPeriod(store: Pick<Store, 'products'> | null | undefined): string | null {
  const kind = resolveProductKind(store);
  if (!kind || kind === 'voucher') return null;
  const months = store?.products?.[kind]?.expiryInMonths;
  if (typeof months !== 'number' || !Number.isInteger(months) || months <= 0) return null;
  const period = months % 12 === 0 ? `${months / 12} år` : `${months} ${months === 1 ? 'måned' : 'måneder'}`;
  return `${period} fra kjøpsdato`;
}

export interface RedemptionCopy {
  icon: string;
  titleShort: string;
  titleLong: string;
  sub: string;
  appStep: string;
  finalStep: string;
}

// All strings Norwegian. Brand name is a parameter — no brand literal may
// appear in this file. Voucher copy is kept neutral between web and
// physical-store redemption, since a reseller can offer both.
export const REDEMPTION_COPY: Record<RedemptionStyle, (vendorName: string, canStoreInApp: boolean) => RedemptionCopy> = {
  wallet: () => ({
    icon: 'phone-portrait-outline',
    titleShort: 'På telefon',
    titleLong: 'Klar på telefonen',
    sub: 'Apple, Google & Samsung Pay',
    appStep: 'Legger kortet i Mine Gavekort-appen, der saldo og transaksjoner vises.',
    finalStep: 'Viderefører Visa-kortet til digital lommebok og betaler med telefonen mot terminalen.',
  }),
  'in-store-qr': () => ({
    icon: 'qr-code-outline',
    titleShort: 'QR-betaling',
    titleLong: 'Klar i kassen',
    sub: 'Betal i butikk med QR-kode',
    appStep: 'Legger kortet i Mine Gavekort-appen, der saldo og transaksjoner vises.',
    finalStep: 'Betaler med gavekortet i kassen, rett fra appen eller PDF-en.',
  }),
  'voucher-code': (vendorName: string, canStoreInApp: boolean) => ({
    icon: 'ticket-outline',
    // The app claim follows canStoreInApp, NOT the redemption style. Voucher is
    // the one kind whose canStoreInApp comes from the API (storeInAppFromApi),
    // so on a store with allowUserStoreInApp false the template suppresses the
    // app step — and this short title must not still advertise the app.
    titleShort: canStoreInApp ? 'Kode i appen' : 'Gavekode',
    titleLong: 'Klar som kode',
    sub: `Brukes hos ${vendorName}`,
    appStep: 'Legger kortet i Mine Gavekort-appen, der koden ligger klar.',
    finalStep: `Bruker koden ved kjøp hos ${vendorName}.`,
  }),
};
